import { useMemo, useRef, useState } from 'react';
import { FileText, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { cn } from '@/lib/utils';
import { useKnowledgeTags, useUploadKnowledgeDocument } from '../queries';
import { KB_TAG_OPTIONS, KB_TAG_SLUGS } from '../tagCatalog';

const ACCEPT = '.md,.pdf,.docx,.txt';

/** Upload a file into a knowledge base. Drag/drop or click to pick. */
export function UploadDocumentDialog({
  knowledgeBaseId,
  open,
  onOpenChange,
}: {
  knowledgeBaseId: number;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const upload = useUploadKnowledgeDocument();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);

  // Canonical catalog + any tags already used in this org's KB (deduped).
  const tagsQuery = useKnowledgeTags(open);
  const tagOptions: MultiSelectOption[] = useMemo(() => {
    const inCatalog = new Set(KB_TAG_SLUGS);
    const extra = (tagsQuery.data ?? [])
      .filter((t) => !inCatalog.has(t))
      .sort()
      .map((value) => ({ value, group: 'In use' }));
    return [...KB_TAG_OPTIONS, ...extra];
  }, [tagsQuery.data]);

  function pickFile(next: File | null) {
    setFile(next);
    // Default the title to the file's name (sans extension) if the user hasn't typed one.
    if (next && !title.trim()) setTitle(next.name.replace(/\.[^.]+$/, ''));
  }

  const submit = () => {
    if (!file) return;
    upload.mutate(
      { knowledgeBaseId, title: title.trim() || file.name, file, tags },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload document</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pickFile(e.dataTransfer.files?.[0] ?? null);
            }}
            className={cn(
              'flex w-full flex-col items-center gap-1 rounded-md border border-dashed p-6 text-sm transition-colors',
              dragging ? 'border-primary bg-accent/40' : 'border-input hover:bg-accent/30',
            )}
          >
            {file ? (
              <span className="flex items-center gap-2">
                <FileText className="text-muted-foreground size-4" />
                <span className="max-w-[16rem] truncate">{file.name}</span>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear file"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      setFile(null);
                    }
                  }}
                >
                  <X className="size-4" />
                </span>
              </span>
            ) : (
              <>
                <Upload className="text-muted-foreground size-5" />
                <span className="text-muted-foreground">
                  Drag a file here, or click to upload
                </span>
                <span className="text-muted-foreground text-xs">.md, .pdf, .docx, .txt</span>
              </>
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />

          <div className="space-y-1.5">
            <Label htmlFor="doc-title">Title</Label>
            <Input
              id="doc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="doc-tags">Tags (optional)</Label>
            <p className="text-muted-foreground text-xs">
              Let agents scope retrieval to this document. Leave empty to include it everywhere.
            </p>
            <MultiSelect
              id="doc-tags"
              value={tags}
              onChange={setTags}
              options={tagOptions}
              creatable
              placeholder="No tags"
              searchPlaceholder="Search or add a tag…"
              emptyText="No tags yet — type to add one."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!file || upload.isPending}>
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
